import {IStorageDriver} from '@src/@interfaces/services/Storage/IStorageDriver';
import TemporaryStorage from './drivers/TemporaryStorage';
import {IListener, ISubscription} from '@src/@interfaces/services/Events';
import {IEventEmitter} from '@src/@interfaces/services/Events/IEventEmitter';
import EventEmitter from '@src/services/Events/EventEmitter';
import {IJsonStorage} from '@src/@interfaces/services/Storage/IJsonStorage';
import {parseJsonSilently} from '@src/helpers/json/parseJsonSilently';

const defaultStorageDriver = window?.localStorage ?? new TemporaryStorage();

export default class JsonStorage<
    TValues extends Record<string, any> = Record<string, any>,
    TKey extends Extract<keyof TValues, string> = Extract<keyof TValues, string>
> extends EventEmitter<'changed'> implements IJsonStorage<TValues, TKey>, IEventEmitter {
    constructor(
        protected readonly prefix: string = '',
        protected readonly driver: IStorageDriver = defaultStorageDriver
    ) {
        super();
    }

    get<K extends TKey>(name: K): TValues[K] | null {
        return this.parseJson(
            this.driver.getItem(this.prefix + name)
        );
    }

    has(name: TKey): boolean {
        return this.get(name) !== null;
    }

    set<K extends TKey>(name: K, value: TValues[K] | null): void {
        this.driver.setItem(this.prefix + name, JSON.stringify(value));
        this.emit('changed', {name, realName: this.prefix + name, value});
    }

    remove(name: TKey): void {
        this.driver.removeItem(this.prefix + name);

        this.emit('changed', {name, realName: this.prefix + name, value: null});
    }

    getList(): Partial<TValues> {
        const result: Partial<TValues> = {};
        for (let i = 0; i < this.driver.length; i++) {
            const key = this.driver.key(i);
            if (key && key.startsWith(this.prefix)) {
                result[key.replace(this.prefix, '') as TKey] = this.parseJson(
                    this.driver.getItem(key)
                );
            }
        }

        return result;
    }

    onChanged<K extends TKey>(listener: IListener<{ name: K, value: TValues[K] | null }>): ISubscription {
        return this.on('changed', listener);
    }

    clear(): void {
        for (let i = 0; i < this.driver.length; i++) {
            const key = this.driver.key(i);
            if (key && key.startsWith(this.prefix)) {
                this.remove(
                    key.substring(this.prefix.length) as TKey
                );
            }
        }
    }

    protected parseJson(json: string | undefined | null) {
        return json != null ? parseJsonSilently(json) : null;
    }
}
